import type { LucideIcon } from 'lucide-react'
import * as React from 'react'
import { clsx } from 'clsx'
import styles from '../Icon.module.css'
import {
  computeStrokeWidth,
  computeSize,
  getIconColorClassName,
} from '../helpers'
import type { IconProps, IconComponentRef } from '../helpers'

const toKebabCase = (str: string): string =>
  str
    .replace(/Icon$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()

/**
 * Wraps a Lucide icon with Buffer's styling system (design tokens, sizing, stroke width).
 *
 * @param IconComponent - The Lucide icon component to wrap
 * @param displayName - Display name for React DevTools and `data-icon` attribute.
 *   Defaults to the Lucide component's `displayName`.
 */
export const icon = (
  IconComponent: LucideIcon,
  displayName: string = IconComponent.displayName ?? '',
) => {
  const iconName = toKebabCase(displayName)
  const WrappedIcon = React.forwardRef(
    (
      { size, stroke, className, color, ...props }: IconProps,
      ref: IconComponentRef,
    ) => {
      const computedSize = computeSize(size)
      const computedStrokeWidth = computeStrokeWidth(computedSize, stroke)
      return (
        <IconComponent
          ref={ref}
          data-icon={iconName || undefined}
          strokeWidth={computedStrokeWidth}
          width={computedSize}
          height={computedSize}
          aria-hidden="true"
          className={clsx(styles.base, getIconColorClassName(color), className)}
          {...props}
        />
      )
    },
  )
  WrappedIcon.displayName = displayName
  return WrappedIcon
}
